import React, { useState, useEffect } from 'react';
import { getArbicsxPropCosts, addArbicsxPropCost, updateArbicsxPropCost, deleteArbicsxPropCost, withdrawArbicsxPropCosts, getArbicsxSettings } from '../../lib/api';
import { useModule } from '../../contexts/ModuleContext';
import { formatCurrency, formatDate, getTodayString } from '../../lib/utils';
import { PropFirmLogo } from '../../lib/propFirmLogos';
import { IdentityColorDot } from '../../lib/identityColors';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Pencil, Trash2, Receipt, Wallet, ArrowDownCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ArbicsxCostiContiProp() {
  const { impersonatedUser, isImpersonating } = useModule();
  const [costs, setCosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [showModal, setShowModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [formData, setFormData] = useState({
    date: getTodayString(),
    prop_firm: '',
    identity: '',
    amount: '',
    status: 'da_prelevare'
  });

  const impersonateId = isImpersonating ? impersonatedUser?.id : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [costsRes, settingsRes] = await Promise.all([
        getArbicsxPropCosts(impersonateId, page, 100),
        getArbicsxSettings(impersonateId)
      ]);
      const costsData = costsRes.data;
      setCosts(costsData.items || []);
      setPagination({ total: costsData.total || 0, pages: costsData.pages || 1 });
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [impersonateId, page]);

  const daPrelevareCosts = costs.filter(c => c.status === 'da_prelevare');
  const prelevatoCosts = costs.filter(c => c.status === 'prelevato');
  
  const totalDaPrelevare = daPrelevareCosts.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  const totalPrelevato = prelevatoCosts.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

  const handleSubmit = async () => {
    if (!formData.date || !formData.amount) {
      toast.error('Compila i campi obbligatori');
      return;
    }

    try {
      if (editingCost) {
        await updateArbicsxPropCost(editingCost.id, formData, impersonateId);
        toast.success('Costo aggiornato');
      } else {
        await addArbicsxPropCost(formData, impersonateId);
        toast.success('Costo aggiunto');
      }
      setShowModal(false);
      setEditingCost(null);
      setFormData({ date: getTodayString(), prop_firm: '', identity: '', amount: '', status: 'da_prelevare' });
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);
    setFormData({
      date: cost.date,
      prop_firm: cost.prop_firm || '',
      identity: cost.identity || '',
      amount: cost.amount?.toString() || '',
      status: cost.status || 'da_prelevare'
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteArbicsxPropCost(deleteId, impersonateId);
      toast.success('Costo eliminato');
      setShowDeleteDialog(false);
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    if (amount > totalDaPrelevare) {
      toast.error('Importo superiore al totale da prelevare');
      return;
    }

    try {
      await withdrawArbicsxPropCosts(amount, impersonateId);
      toast.success(`${formatCurrency(amount)} segnati come prelevati`);
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Righe sottili: etichetta a sinistra, importo e azioni a destra
  const renderCostsRows = (costsList) => (
    <div className="ht-list">
      {costsList.map(cost => (
        <div key={cost.id} className="ht-item" data-testid={`cost-row-${cost.id}`}>
          <div className="ht-item-main">
            <p className="ht-item-t flex items-center gap-2">
              <PropFirmLogo name={cost.prop_firm} size="xs" />
              <span className="truncate">{cost.prop_firm || '-'}</span>
            </p>
            <p className="ht-item-s flex items-center gap-1.5">
              <IdentityColorDot identity={cost.identity} />
              <span className="truncate">{cost.identity || '-'}</span>
              <span className="text-[#5f6672]">· {formatDate(cost.date)}</span>
            </p>
          </div>
          <span className="ht-item-n">{formatCurrency(cost.amount || 0)}</span>
          <div className="ht-item-actions">
            <button
              type="button"
              onClick={() => handleEdit(cost)}
              className="ht-btn ht-btn-icon"
              aria-label="Modifica costo"
            >
              <Pencil size={13} />
            </button>
            <button
              type="button"
              onClick={() => {
                setDeleteId(cost.id);
                setShowDeleteDialog(true);
              }}
              className="ht-btn ht-btn-icon ht-btn-danger"
              aria-label="Elimina costo"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ht-page-h1">Costi da Recuperare</h1>
          <p className="ht-page-sub">
            Arbicsx {isImpersonating && `- ${impersonatedUser?.name}`}
          </p>
        </div>
        {totalDaPrelevare > 0 && (
          <button
            type="button"
            onClick={() => setShowWithdrawModal(true)}
            className="ht-btn ht-btn-primary self-start sm:self-auto"
            data-testid="withdraw-btn"
          >
            <ArrowDownCircle size={14} />
            Preleva
          </button>
        )}
      </div>

      {/* Sintesi */}
      <div className="ht-panel">
        <div className="ht-hero">
          <div className="ht-hero-main">
            <span className="ht-hero-label">Da prelevare</span>
            <p className={`ht-hero-num ${totalDaPrelevare > 0 ? 'ht-warn' : ''}`} data-testid="total-da-prelevare">
              {formatCurrency(totalDaPrelevare)}
            </p>
          </div>
          <div className="ht-hero-side ht-rows">
            <div className="ht-row">
              <span className="ht-row-label"><Receipt size={12} /> Voci da prelevare</span>
              <span className="ht-row-value">{daPrelevareCosts.length}</span>
            </div>
            <div className="ht-row">
              <span className="ht-row-label"><Wallet size={12} /> Già prelevato</span>
              <span className="ht-row-value ht-pos" data-testid="total-prelevato">{formatCurrency(totalPrelevato)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="da_prelevare" className="w-full">
        <TabsList className="ht-tabs bg-transparent border-0 p-0 h-auto">
          <TabsTrigger value="da_prelevare" className="ht-tab">
            Da Prelevare ({daPrelevareCosts.length})
          </TabsTrigger>
          <TabsTrigger value="prelevato" className="ht-tab">
            Già Prelevato ({prelevatoCosts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="da_prelevare" className="mt-4">
          <div className="ht-panel overflow-hidden">
            {daPrelevareCosts.length === 0 ? (
              <div className="ht-empty">Nessun costo da prelevare</div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#282c34]">
                  <span className="text-xs text-[#8b919c]">
                    Dettaglio per slot · {daPrelevareCosts.length} {daPrelevareCosts.length === 1 ? 'voce' : 'voci'}
                  </span>
                  <span className="ht-item-n ht-warn">{formatCurrency(totalDaPrelevare)}</span>
                </div>
                {renderCostsRows(daPrelevareCosts)}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="prelevato" className="mt-4">
          <div className="ht-panel overflow-hidden">
            {prelevatoCosts.length === 0 ? (
              <div className="ht-empty">Nessun costo prelevato</div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#282c34]">
                  <span className="text-xs text-[#8b919c]">
                    Dettaglio per slot · {prelevatoCosts.length} {prelevatoCosts.length === 1 ? 'voce' : 'voci'}
                  </span>
                  <span className="ht-item-n ht-pos">{formatCurrency(totalPrelevato)}</span>
                </div>
                {renderCostsRows(prelevatoCosts)}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="ht-panel-title text-base">
              {editingCost ? 'Modifica Costo' : 'Aggiungi Costo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Data</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Prop Firm</Label>
              <Select value={formData.prop_firm} onValueChange={(v) => setFormData({ ...formData, prop_firm: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent className="bg-[#11161d] border-[#282c34]">
                  {settings?.prop_firms?.map((p, idx) => (
                    <SelectItem key={idx} value={p.name} className="text-white">{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Identità</Label>
              <Select value={formData.identity} onValueChange={(v) => setFormData({ ...formData, identity: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent className="bg-[#11161d] border-[#282c34]">
                  {settings?.identities?.map((i, idx) => (
                    <SelectItem key={idx} value={i.name} className="text-white">{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Importo ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="0.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Stato</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#11161d] border-[#282c34]">
                  <SelectItem value="da_prelevare" className="text-white">Da Prelevare</SelectItem>
                  <SelectItem value="prelevato" className="text-white">Già Prelevato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button type="button" className="ht-btn" onClick={() => setShowModal(false)}>
              Annulla
            </button>
            <button type="button" onClick={handleSubmit} className="ht-btn ht-btn-primary" data-testid="save-cost-btn">
              {editingCost ? 'Aggiorna' : 'Aggiungi'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="ht-panel-title text-base">Preleva Costi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="ht-row">
              <span className="ht-row-label">Totale da prelevare</span>
              <span className="ht-row-value ht-warn">{formatCurrency(totalDaPrelevare)}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Importo da prelevare ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="0.00"
                max={totalDaPrelevare}
              />
            </div>
          </div>
          <DialogFooter>
            <button type="button" className="ht-btn" onClick={() => setShowWithdrawModal(false)}>
              Annulla
            </button>
            <button type="button" onClick={handleWithdraw} className="ht-btn ht-btn-primary" data-testid="confirm-withdraw-btn">
              Conferma Prelievo
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="ht-panel-title text-base">Eliminare questo costo?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-[#8b919c]">
              Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="ht-btn border-0">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="ht-btn ht-btn-danger">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
